import React from 'react';
import { useIntl, defineMessages } from 'react-intl-next';

import { cssMap } from '@atlaskit/css';
import { Box, Inline, Stack, Text } from '@atlaskit/primitives';
import Button from '@atlaskit/button/new';
import VideoPlayOverlayIcon from '@atlaskit/icon/core/video-play-overlay';
import { token } from '@atlaskit/tokens';
import Image from '@atlaskit/image';

import { useSetOnboardingState } from '@confluence/onboarding-helpers/entry-points/hooks/useOnboardingState';
import { ONBOARDING_INSIGHTS } from '@confluence/onboarding-helpers/entry-points/constants/onboarding-state-constants';

import AudioBriefingChangeboardingIllustration from '../../assets/AudioBriefingChangeboardingIllustration.svg';

const styles = cssMap({
	wrapper: {
		width: '300px',
	},
	buttonWrapper: {
		paddingTop: token('space.200'),
		paddingBottom: token('space.150'),
	},
	audioBriefingIllustrationContainer: {
		paddingTop: token('space.075'),
		paddingBottom: token('space.250'),
		width: '81px',
		height: '62px',
	},
});

const i18n = defineMessages({
	audioBriefingChangeboardingHeader: {
		id: 'audio.briefing-popup.changeboarding-header',
		description: 'Header for introducing the audio briefings feature',
		defaultMessage: 'Introducing audio briefings',
	},
	audioBriefingChangeboardingBody: {
		id: 'audio.briefing-popup.changeboarding-body',
		description: 'Description for audio briefings',
		defaultMessage:
			'Briefings are like short podcasts that help you get up to speed on everything relating to this page.',
	},
	audioBriefingChangeboardingListenButton: {
		id: 'audio.briefing-popup.changeboarding.button.listen',
		description: 'Text for button that allows users to acknowledge audio briefing changeboard',
		defaultMessage: 'Listen now',
	},
});

export const AudioBriefingChangeboarding = () => {
	const { formatMessage } = useIntl();
	const { setOnboardingState } = useSetOnboardingState();

	const onClick = async () => {
		setOnboardingState({
			key: ONBOARDING_INSIGHTS.AUDIO_BRIEFING,
			value: 'true',
		});
	};

	return (
		<Box xcss={styles.wrapper} padding="space.200">
			<Stack space="space.050" alignInline="center">
				<Inline
					xcss={styles.audioBriefingIllustrationContainer}
					alignInline="center"
					alignBlock="center"
				>
					<Image src={AudioBriefingChangeboardingIllustration} alt="" />
				</Inline>

				<Text color="color.text" align="center" size="large" weight="bold">
					{formatMessage(i18n.audioBriefingChangeboardingHeader)}
				</Text>
				<Text color="color.text.subtle" align="center" size="medium">
					{formatMessage(i18n.audioBriefingChangeboardingBody)}
				</Text>

				<Inline alignInline="center" xcss={styles.buttonWrapper}>
					<Box paddingInline="space.150" paddingBlock="space.050">
						<Button
							iconBefore={(iconProps) => (
								<VideoPlayOverlayIcon {...iconProps} color={token('color.icon.selected')} />
							)}
							onClick={onClick}
						>
							<Text>{formatMessage(i18n.audioBriefingChangeboardingListenButton)}</Text>
						</Button>
					</Box>
				</Inline>
			</Stack>
		</Box>
	);
};
